import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Scale, Plug, CheckCircle2, ExternalLink, ArrowRight, Settings2, Play, Download, Info, AlertTriangle, RefreshCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { api, listResource, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fdate, fdatetime, mxn } from "@/lib/format";
import { formatDomicilio } from "@/lib/domicilio";

const MODO_META = {
  mock: { label: "Modo demo (simulación)", cls: "border-slate-200 bg-slate-100 text-slate-600" },
  sandbox: { label: "Sandbox (envío real)", cls: "border-amber-200 bg-amber-100 text-amber-700" },
  produccion: { label: "Producción (envío real)", cls: "border-forest-600/30 bg-forest-600/15 text-forest-700" },
};

// Feb-2026 · CONDUSEF/RECO — mes anterior por defecto.
const _hoy = new Date();
const _mesAnterior = () => {
  const d = new Date(_hoy.getFullYear(), _hoy.getMonth() - 1, 1);
  return { anio: d.getFullYear(), mes: d.getMonth() + 1 };
};

export default function Condusef() {
  const { user } = useAuth();
  const [sipres, setSipres] = useState([]);
  const [reca, setReca] = useState([]);
  const [reune, setReune] = useState([]);
  const [redeco, setRedeco] = useState([]);
  const [cond, setCond] = useState(null);

  // ── RECO state (Feb-2026 · Msg 246) ──
  const _def = _mesAnterior();
  const [recoAnio, setRecoAnio] = useState(_def.anio);
  const [recoMes, setRecoMes] = useState(_def.mes);
  const [recoUmbral, setRecoUmbral] = useState(90);
  const [recoUmbralDefault, setRecoUmbralDefault] = useState(90);
  const [recoPreview, setRecoPreview] = useState(null);
  const [recoBusy, setRecoBusy] = useState(false);
  const [recoHistorico, setRecoHistorico] = useState([]);
  const [recoEquiv, setRecoEquiv] = useState([]);
  const canGenerarReco = user?.role === "oficial_cumplimiento" || user?.role === "admin_entidad";
  const canEditarUmbral = user?.role === "admin_entidad";

  const cargarRecoConfig = () => {
    if (!canGenerarReco) return;
    api.get("/condusef/reco/config").then((r) => {
      setRecoUmbral(r.data.umbral_dias_vencido || 90);
      setRecoUmbralDefault(r.data.umbral_default || 90);
    }).catch(() => {});
    api.get("/condusef/reco/reportes").then((r) => setRecoHistorico(r.data.reportes || [])).catch(() => {});
    api.get("/condusef/reco/equivalencias").then((r) => setRecoEquiv(r.data.equivalencias || [])).catch(() => {});
  };

  useEffect(() => {
    listResource("condusef_sipres").then(setSipres);
    listResource("condusef_reca").then(setReca);
    listResource("condusef_reune").then(setReune);
    listResource("condusef_redeco").then(setRedeco);
    api.get("/integraciones/credenciales/condusef").then((r) => setCond(r.data)).catch(() => {});
    cargarRecoConfig();
  }, [user?.role]);

  const generarPreview = async () => {
    setRecoBusy(true);
    try {
      const { data } = await api.get("/condusef/reco/preview", { params: { anio: recoAnio, mes: recoMes, umbral_dias: recoUmbral } });
      setRecoPreview(data);
      toast.success(`Vista previa RECO ${data.periodo} generada · ${data.filas.length} fila(s).`);
    } catch (e) { toast.error(apiError(e)); } finally { setRecoBusy(false); }
  };

  const guardarReporte = async () => {
    setRecoBusy(true);
    try {
      const { data } = await api.post("/condusef/reco/generar", { anio: recoAnio, mes: recoMes, umbral_dias_vencido: recoUmbral });
      toast.success(`Reporte RECO ${data.periodo} guardado en el histórico.`);
      setRecoHistorico((prev) => [data, ...prev]);
      setRecoPreview(data);
    } catch (e) { toast.error(apiError(e)); } finally { setRecoBusy(false); }
  };

  const guardarUmbral = async () => {
    const n = Number(recoUmbral);
    if (!Number.isInteger(n) || n <= 0) { toast.error("El umbral debe ser un entero positivo."); return; }
    setRecoBusy(true);
    try {
      await api.put("/condusef/reco/config", { umbral_dias_vencido: n });
      toast.success(`Umbral RECO guardado en ${n} días.`);
    } catch (e) { toast.error(apiError(e)); } finally { setRecoBusy(false); }
  };

  const exportarExcel = async (reporteId) => {
    try {
      const resp = await api.get(`/condusef/reco/${reporteId}/exportar`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `RECO_${reporteId}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const copiarAlPortapapeles = () => {
    if (!recoPreview) return;
    const rows = [["Tipo interno", "Clave CONDUSEF", "Descripción", "Cartera vigente", "Cartera vencida", "Contratos periodo", "Contratos vigentes"]];
    (recoPreview.filas || []).forEach((f) => rows.push([f.tipo_interno, f.clave_condusef || "—", f.descripcion_condusef,
      f.monto_cartera_vigente, f.monto_cartera_vencida, f.contratos_celebrados_periodo, f.contratos_vigentes]));
    const tsv = rows.map((r) => r.join("\t")).join("\n");
    navigator.clipboard.writeText(tsv).then(() => toast.success("Tabla copiada. Pega en el portal CONDUSEF."));
  };

  const s = sipres[0];
  const modo = cond?.modo || "mock";
  const eb = MODO_META[modo] || MODO_META.mock;

  return (
    <div>
      <PageHeader title="CONDUSEF" subtitle="SIPRES · RECA · RECO · REUNE · REDECO — atención al usuario financiero" />
      <div className="p-6 md:p-8">
        <Tabs defaultValue="sipres">
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="sipres" data-testid="tab-sipres">SIPRES</TabsTrigger>
            <TabsTrigger value="reca" data-testid="tab-reca">RECA</TabsTrigger>
            <TabsTrigger value="reco" data-testid="tab-reco">RECO</TabsTrigger>
            <TabsTrigger value="reune" data-testid="tab-reune">REUNE</TabsTrigger>
            <TabsTrigger value="redeco" data-testid="tab-redeco">REDECO</TabsTrigger>
            <TabsTrigger value="api" data-testid="tab-api">Conexión API</TabsTrigger>
          </TabsList>

          <TabsContent value="sipres">
            {s ? (
              <div className="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:grid-cols-2 lg:grid-cols-3">
                {[
                  ["Clave SIPRES", s.clave_sipres], ["Denominación", s.denominacion], ["Sector", s.sector],
                  ["Representante", s.representante], ["UNE", s.une], ["Teléfono", s.telefono],
                  ["Correo", s.correo], ["Estatus", s.estatus], ["Última actualización", fdate(s.ultima_actualizacion)],
                  ["Domicilio", formatDomicilio(s.domicilio)], ["Evidencia", s.evidencia],
                ].map(([l, v]) => (
                  <div key={l}>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">{l}</p>
                    <p className="mt-0.5 text-sm font-medium text-slate-800">{v || "—"}</p>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">Sin registro SIPRES.</p>}
          </TabsContent>

          <TabsContent value="reca">
            <DataTable testid="table-reca" rows={reca}
              columns={[
                { key: "producto", label: "Producto" }, { key: "tipo_contrato", label: "Tipo contrato" },
                { key: "numero_registro", label: "No. registro" }, { key: "fecha_registro", label: "Fecha", kind: "date" },
                { key: "version", label: "Versión" }, { key: "estatus", label: "Estatus", kind: "badge" },
              ]} emptyLabel="Sin contratos RECA" />
          </TabsContent>

          <TabsContent value="reco">
            {/* Feb-2026 · Msg 246 — Generador de reporte RECO (calculadora, portal
                CONDUSEF sigue siendo captura manual). Se muestra sólo a OC y admin. */}
            {canGenerarReco ? (
              <div className="space-y-4">
                {/* Config + Generador */}
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="reco-generador-card">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-heading text-base font-semibold text-slate-900">Generar reporte RECO</h3>
                    <Badge className="border border-sky-200 bg-sky-50 text-sky-700"><Info className="mr-1 h-3 w-3" /> Portal CONDUSEF: captura manual</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                    <div>
                      <Label className="mb-1 block text-xs">Año</Label>
                      <Input data-testid="reco-anio" type="number" min="2000" max="2100" value={recoAnio} onChange={(e) => setRecoAnio(Number(e.target.value) || _def.anio)} />
                    </div>
                    <div>
                      <Label className="mb-1 block text-xs">Mes</Label>
                      <Input data-testid="reco-mes" type="number" min="1" max="12" value={recoMes} onChange={(e) => setRecoMes(Math.max(1, Math.min(12, Number(e.target.value) || _def.mes)))} />
                    </div>
                    <div>
                      <Label className="mb-1 block text-xs">Umbral días vencido (RECO)</Label>
                      <Input data-testid="reco-umbral" type="number" min="1" value={recoUmbral} onChange={(e) => setRecoUmbral(Number(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="mb-1 block text-xs">Moneda</Label>
                      <Input data-testid="reco-moneda" value="MXN" disabled />
                    </div>
                    <div className="flex items-end gap-2">
                      <Button data-testid="reco-preview-btn" disabled={recoBusy} onClick={generarPreview} className="bg-primary hover:bg-sidebar-hover">
                        <Play className="mr-1 h-4 w-4" /> {recoBusy ? "…" : "Vista previa"}
                      </Button>
                    </div>
                  </div>
                  {canEditarUmbral && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Button data-testid="reco-guardar-umbral" size="sm" variant="outline" disabled={recoBusy} onClick={guardarUmbral}>
                        Guardar umbral por defecto
                      </Button>
                      <span className="text-xs text-slate-500">Default de plataforma: {recoUmbralDefault} días.</span>
                    </div>
                  )}
                  <Alert className="mt-3 border-amber-200 bg-amber-50" data-testid="reco-criterio-alert">
                    <AlertTriangle className="h-4 w-4 text-amber-700" />
                    <AlertDescription className="text-xs text-amber-900">
                      El criterio de <strong>vencido</strong> para el RECO es <strong>peor atraso &gt; {recoUmbral} días</strong>. Este criterio SÓLO
                      aplica al reporte RECO — la clasificación del sistema (cobranza) sigue marcando vencido con 1 día de atraso.
                      La cifra del RECO puede diferir de lo que ves en cobranza; es esperado.
                    </AlertDescription>
                  </Alert>
                </div>

                {/* Preview */}
                {recoPreview && (
                  <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="reco-preview-card">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-heading text-base font-semibold text-slate-900">Reporte RECO {recoPreview.periodo}</p>
                        <p className="text-xs text-slate-500">
                          Fecha de corte: <strong>{recoPreview.fecha_corte}</strong> · Umbral vencido:{" "}
                          <strong>{recoPreview.umbral_dias_vencido} días</strong> · Moneda: <strong>{recoPreview.moneda}</strong>
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button data-testid="reco-copiar-btn" size="sm" variant="outline" onClick={copiarAlPortapapeles}>
                          Copiar tabla (TSV para portal)
                        </Button>
                        <Button data-testid="reco-guardar-btn" size="sm" disabled={recoBusy} onClick={guardarReporte} className="bg-forest-600 hover:bg-forest-700">
                          <CheckCircle2 className="mr-1 h-4 w-4" /> Guardar en histórico
                        </Button>
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                          <th className="py-2 pl-2">Tipo interno</th>
                          <th className="py-2">Clave CONDUSEF</th>
                          <th className="py-2">Descripción</th>
                          <th className="py-2 text-right">Cartera vigente</th>
                          <th className="py-2 text-right">Cartera vencida</th>
                          <th className="py-2 text-right">Contratos periodo</th>
                          <th className="py-2 text-right">Contratos vigentes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recoPreview.filas.map((f, i) => (
                          <tr key={i} data-testid={`reco-fila-${f.tipo_interno}`} className={`border-b border-slate-100 ${f.sin_equivalencia ? "bg-amber-50" : ""}`}>
                            <td className="py-2 pl-2 font-medium text-slate-800">
                              {f.tipo_interno}
                              {f.sin_equivalencia && <Badge className="ml-2 border border-amber-300 bg-amber-100 text-amber-800" data-testid={`reco-sin-equiv-${f.tipo_interno}`}>Sin equivalencia validada</Badge>}
                            </td>
                            <td className="py-2 font-mono text-xs">{f.clave_condusef || "—"}</td>
                            <td className="py-2 text-slate-600">{f.descripcion_condusef}</td>
                            <td className="py-2 text-right font-mono">{mxn(f.monto_cartera_vigente)}</td>
                            <td className={`py-2 text-right font-mono ${f.monto_cartera_vencida > 0 ? "text-red-700" : ""}`}>{mxn(f.monto_cartera_vencida)}</td>
                            <td className="py-2 text-right font-mono">{f.contratos_celebrados_periodo}</td>
                            <td className="py-2 text-right font-mono">{f.contratos_vigentes}</td>
                          </tr>
                        ))}
                        <tr data-testid="reco-fila-total" className="bg-forest-600/5 font-semibold">
                          <td className="py-2 pl-2" colSpan={3}>Total</td>
                          <td className="py-2 text-right font-mono">{mxn(recoPreview.totales.monto_cartera_vigente)}</td>
                          <td className="py-2 text-right font-mono">{mxn(recoPreview.totales.monto_cartera_vencida)}</td>
                          <td className="py-2 text-right font-mono">{recoPreview.totales.contratos_celebrados_periodo}</td>
                          <td className="py-2 text-right font-mono">{recoPreview.totales.contratos_vigentes}</td>
                        </tr>
                      </tbody>
                    </table>
                    {(recoPreview.creditos_no_procesados || []).length > 0 && (
                      <Alert className="mt-3 border-red-200 bg-red-50">
                        <AlertTriangle className="h-4 w-4 text-red-700" />
                        <AlertDescription className="text-xs text-red-900">
                          {recoPreview.creditos_no_procesados.length} crédito(s) no se pudieron reconstruir al corte. Revisa la bitácora.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}

                {/* Histórico */}
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="reco-historico-card">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-heading text-base font-semibold text-slate-900">Histórico de reportes RECO</h3>
                    <Button size="sm" variant="ghost" onClick={cargarRecoConfig}><RefreshCcw className="mr-1 h-4 w-4" /> Refrescar</Button>
                  </div>
                  <DataTable testid="table-reco-historico" rows={recoHistorico}
                    emptyLabel="Aún no hay reportes RECO guardados."
                    columns={[
                      { key: "periodo", label: "Periodo" },
                      { key: "fecha_corte", label: "Corte", kind: "date" },
                      { key: "umbral_dias_vencido", label: "Umbral (d)", render: (r) => <span className="font-mono">{r.umbral_dias_vencido}</span> },
                      { key: "cartera_vigente", label: "Cartera vigente", render: (r) => <span className="font-mono">{mxn(r?.totales?.monto_cartera_vigente || 0)}</span> },
                      { key: "cartera_vencida", label: "Cartera vencida", render: (r) => <span className="font-mono">{mxn(r?.totales?.monto_cartera_vencida || 0)}</span> },
                      { key: "contratos", label: "Contratos vig.", render: (r) => r?.totales?.contratos_vigentes || 0 },
                      { key: "generado_en", label: "Generado", render: (r) => <span className="text-xs">{fdatetime(r.generado_en)}<br /><span className="text-slate-500">por {r.generado_por_nombre || r.generado_por}</span></span> },
                    ]}
                    actions={(row) => (
                      <Button size="sm" variant="outline" data-testid={`reco-exportar-${row.id}`} onClick={() => exportarExcel(row.id)}>
                        <Download className="mr-1 h-4 w-4" /> Excel
                      </Button>
                    )} />
                </div>

                {/* Estado de equivalencias (informativo) */}
                <details className="rounded-lg border border-slate-200 bg-white p-4 text-sm shadow-sm" data-testid="reco-equivalencias-card">
                  <summary className="cursor-pointer font-heading font-semibold text-slate-800">
                    Estado de equivalencias de tipo de crédito ({recoEquiv.filter((e) => e.validada).length} validadas / {recoEquiv.length} totales)
                  </summary>
                  <p className="mt-2 text-xs text-slate-500">
                    Sólo el super_admin puede validar estas equivalencias. Los tipos internos sin equivalencia validada
                    aparecen agrupados aparte en el reporte.
                  </p>
                  <ul className="mt-2 space-y-1 text-xs">
                    {recoEquiv.map((e) => (
                      <li key={e.tipo_interno} data-testid={`reco-eq-${e.tipo_interno}`} className="flex items-center gap-2">
                        {e.validada
                          ? <Badge className="border border-emerald-300 bg-emerald-100 text-emerald-800">Validada</Badge>
                          : <Badge className="border border-amber-300 bg-amber-100 text-amber-800">Pendiente</Badge>}
                        <span className="font-mono">{e.tipo_interno}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-mono">{e.clave_condusef}</span>
                        <span className="text-slate-600">{e.descripcion_condusef}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            ) : (
              <Alert className="border-slate-200 bg-slate-50">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Sólo el Oficial de Cumplimiento y el administrador de la entidad pueden generar el reporte RECO.
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          <TabsContent value="reune">
            <ReuneBloque cond={cond} />
            <div className="mt-4">
              <DataTable testid="table-reune" rows={reune}
                columns={[
                  { key: "folio_interno", label: "Folio" }, { key: "tipo", label: "Tipo", kind: "badge" },
                  { key: "cliente", label: "Cliente" }, { key: "causa", label: "Causa" },
                  { key: "fecha_recepcion", label: "Recepción", kind: "date" }, { key: "estatus", label: "Estatus", kind: "badge" },
                ]} emptyLabel="Sin registros REUNE" />
            </div>
          </TabsContent>

          <TabsContent value="redeco">
            <RedecoBloque redeco={redeco} setRedeco={setRedeco} cond={cond} />
            <div className="mt-4">
              <DataTable testid="table-redeco" rows={redeco}
                columns={[
                  { key: "despacho", label: "Despacho" }, { key: "cliente", label: "Cliente" },
                  { key: "credito", label: "Crédito" }, { key: "motivo", label: "Motivo" },
                  { key: "fecha", label: "Fecha", kind: "date" }, { key: "estatus", label: "Estatus", kind: "badge" },
                ]} emptyLabel="Sin quejas REDECO" />
            </div>
          </TabsContent>

          <TabsContent value="api">
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sage/25"><Plug className="h-5 w-5 text-forest-600" /></div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-heading text-base font-semibold text-slate-900">Conexión API CONDUSEF (REUNE / REDECO)</h3>
                    <span data-testid="condusef-modo-badge" className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${eb.cls}`}>{eb.label}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    {modo === "mock"
                      ? "La integración opera en modo demo: los envíos REUNE/REDECO se simulan y generan acuses de prueba. No se envía nada a la autoridad."
                      : `Conexión real activa en ${modo}. Los envíos REUNE/REDECO y el cierre trimestral se transmiten a CONDUSEF.`}
                  </p>

                  {cond && (
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                        <p className="text-[11px] uppercase tracking-wide text-slate-500">Autenticación</p>
                        <p className="mt-0.5 font-medium text-slate-800">{cond.auth || "—"}</p>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                        <p className="text-[11px] uppercase tracking-wide text-slate-500">Productos</p>
                        <p className="mt-0.5 font-medium text-slate-800">{(cond.productos || []).join(", ") || "—"}</p>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                        <p className="text-[11px] uppercase tracking-wide text-slate-500">Credenciales</p>
                        <p className="mt-0.5 flex items-center gap-1.5 font-medium text-slate-800">
                          {cond.configurado
                            ? <><CheckCircle2 className="h-4 w-4 text-forest-600" /> Configuradas (cifradas)</>
                            : <span className="text-amber-700">Pendientes de captura</span>}
                        </p>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                        <p className="text-[11px] uppercase tracking-wide text-slate-500">Última prueba</p>
                        <p className="mt-0.5 font-medium text-slate-800">{cond.ultima_prueba ? fdatetime(cond.ultima_prueba) : "Sin pruebas"}</p>
                        {cond.ultimo_resultado ? <p className="mt-0.5 text-xs text-slate-500">{cond.ultimo_resultado}</p> : null}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Button asChild size="sm" className="bg-primary hover:bg-sidebar-hover" data-testid="condusef-ir-integraciones">
                      <Link to="/centro-integraciones">
                        <Settings2 className="mr-1.5 h-4 w-4" /> Configurar / enviar en Centro de Integraciones <ArrowRight className="ml-1.5 h-4 w-4" />
                      </Link>
                    </Button>
                    {cond?.doc && (
                      <a href={`https://${(cond.doc || "").split(" ")[0]}`} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 text-xs text-forest-700 hover:underline">
                        Documentación oficial <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <p className="mt-4 text-xs text-slate-400">
                    La gestión de credenciales, envíos REUNE/REDECO y el cierre trimestral se realizan desde el Centro de Integraciones,
                    con confirmación humana antes de cada envío real a la autoridad.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Feb-2026 · Bloque 4 CONDUSEF — Formulario REDECO + vista de captura manual.
// La vista de captura manual FUNCIONA CON O SIN API — es la razón de ser de la
// pantalla: el OC obtiene los 25 campos ya traducidos a claves oficiales para
// copiar/pegar en el portal https://pur.condusef.gob.mx
// ═══════════════════════════════════════════════════════════════════════════════
function RedecoBloque({ redeco, setRedeco, cond }) {
  const modo = cond?.modo || "mock";
  const conexionReal = modo === "sandbox" || modo === "produccion";

  const [estados, setEstados] = useState([]);
  const [medios, setMedios] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [sentidos, setSentidos] = useState([]);
  const [cps, setCps] = useState([]);
  const [munis, setMunis] = useState([]);
  const [cols, setCols] = useState([]);

  const [form, setForm] = useState({
    folio: "",
    fecha_recepcion: new Date().toISOString().slice(0, 10),
    mes_reportado: new Date().getMonth() + 1,
    medio_recepcion_clave: "",
    nivel_atencion_clave: "",
    causa: "",
    pori: "NO",
    estatus_numerico: 1,
    estado_clave: "",
    codigo_postal: "",
    municipio_clave: "",
    colonia_clave: "",
    localidad_clave: "",
    tipo_persona: 1,
    sexo: "",
    edad: "",
    fecha_resolucion: "",
    fecha_notificacion: "",
    sentido_resolucion: "",
    num_penalizaciones: "",
    penalizacion: "",
    cliente_id: "",
    producto_id: "",
    despacho: "",
    credito: "",
    motivo: "",
  });
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errsBackend, setErrsBackend] = useState([]);
  const [confirmEnvio, setConfirmEnvio] = useState(false);

  // Cargar catálogos base al montar
  useEffect(() => {
    api.get("/catalogos-condusef/ENTIDAD_FEDERATIVA").then((r) => setEstados(r.data.registros || [])).catch(() => {});
    api.get("/catalogos-condusef/MEDIO_RECEPCION").then((r) => setMedios(r.data.registros || [])).catch(() => {});
    api.get("/catalogos-condusef/NIVEL_ATENCION").then((r) => setNiveles(r.data.registros || [])).catch(() => {});
    api.get("/catalogos-condusef/SENTIDO_RESOLUCION").then((r) => setSentidos(r.data.registros || [])).catch(() => {});
  }, []);

  // Encadenado: al elegir estado → cargar sus CP.
  useEffect(() => {
    if (!form.estado_clave) { setCps([]); setMunis([]); setCols([]); return; }
    api.get(`/catalogos-condusef/CODIGO_POSTAL?padre=${form.estado_clave}&limit=8000`)
      .then((r) => setCps(r.data.registros || [])).catch(() => {});
  }, [form.estado_clave]);

  // Encadenado: al elegir CP → cargar municipios (padre=estado) filtrando los que
  // pertenecen a ese CP en la práctica, y colonias (padre=cp).
  useEffect(() => {
    if (!form.estado_clave) { setMunis([]); setCols([]); return; }
    if (!form.codigo_postal) return;
    api.get(`/catalogos-condusef/MUNICIPIO?padre=${form.estado_clave}&limit=8000`)
      .then((r) => setMunis(r.data.registros || [])).catch(() => {});
    api.get(`/catalogos-condusef/COLONIA?padre=${form.codigo_postal}&limit=2000`)
      .then((r) => setCols(r.data.registros || [])).catch(() => {});
  }, [form.codigo_postal, form.estado_clave]);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validarLocal = () => {
    const err = [];
    if (!form.folio) err.push("Folio obligatorio.");
    if (!form.fecha_recepcion) err.push("Fecha de recepción obligatoria.");
    // Fecha dentro del mes reportado
    const [yy, mm] = (form.fecha_recepcion || "").split("-");
    if (yy && mm && Number(mm) !== Number(form.mes_reportado)) {
      err.push(`Fecha de recepción (${form.fecha_recepcion}) debe caer dentro del mes reportado (${form.mes_reportado}).`);
    }
    if (Number(form.estatus_numerico) === 2 && !form.sentido_resolucion) {
      err.push("Estatus Concluido requiere Sentido de resolución.");
    }
    if (Number(form.tipo_persona) === 2 && (form.sexo || form.edad)) {
      err.push("Sexo/Edad no aplican a Persona Moral.");
    }
    return err;
  };

  const armarPreview = async () => {
    const errsLocal = validarLocal();
    if (errsLocal.length) { errsLocal.forEach((e) => toast.warning(e)); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/condusef/redeco/preview-layout", {
        queja: { ...form,
          QuejasNoMes: form.mes_reportado,
          QuejasFolio: form.folio,
          QuejasFecRecepcion: form.fecha_recepcion,
          QuejasMedio: form.medio_recepcion_clave,
          QuejasNivelAT: form.nivel_atencion_clave,
          QuejasCausa: form.causa,
          QuejasPORI: form.pori,
          QuejasEstatus: form.estatus_numerico,
          QuejasEstados: form.estado_clave,
          QuejasMunId: form.municipio_clave,
          QuejasColId: form.colonia_clave,
          QuejasLocId: form.localidad_clave || null,
          QuejasCP: form.codigo_postal,
          QuejasTipoPersona: form.tipo_persona,
          QuejasSexo: form.sexo || null,
          QuejasEdad: form.edad || null,
          QuejasFecResolucion: form.fecha_resolucion || null,
          QuejasFecNotificacion: form.fecha_notificacion || null,
          QuejasRespuesta: form.sentido_resolucion || null,
          QuejasNumPenal: form.num_penalizaciones || null,
          QuejasPenalizacion: form.penalizacion || null,
          cliente_id: form.cliente_id, producto_id: form.producto_id },
      });
      setPreview(data);
      setErrsBackend(data.errores_validacion || []);
      setShowPreview(true);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const copiarCampo = (nombre, valor) => {
    const t = valor === null || valor === undefined ? "" : String(valor);
    navigator.clipboard.writeText(t).then(() => toast.success(`${nombre} copiado.`));
  };
  const copiarTodo = () => {
    if (!preview) return;
    const filas = LAYOUT_HDRS.map(([k]) => `${k}\t${preview.fila[k] ?? ""}`).join("\n");
    navigator.clipboard.writeText(filas).then(() => toast.success("25 campos copiados (TSV)."));
  };

  const enviarConfirmado = async () => {
    setConfirmEnvio(false); setBusy(true);
    try {
      const { data } = await api.post("/condusef/redeco/queja", {
        despacho: form.despacho, cliente_id: form.cliente_id, credito: form.credito,
        motivo: form.motivo, producto_id: form.producto_id, ...form });
      toast.success(`Queja REDECO enviada. Acuse: ${data.acuse || data.folio}`);
      setRedeco((prev) => [data, ...(prev || [])]);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="redeco-form-card">
        <h3 className="font-heading text-base font-semibold text-slate-900">Alta de queja REDECO — layout v4.0</h3>
        <p className="mt-1 text-xs text-slate-500">Selects encadenados desde <code>catalogos_condusef</code>. La vista de captura manual funciona con o sin API.</p>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div><Label className="mb-1 block text-xs">Folio</Label><Input data-testid="redeco-folio" value={form.folio} onChange={(e) => setF("folio", e.target.value)} /></div>
          <div><Label className="mb-1 block text-xs">Mes reportado (1-12)</Label><Input data-testid="redeco-mes" type="number" min="1" max="12" value={form.mes_reportado} onChange={(e) => setF("mes_reportado", Number(e.target.value))} /></div>
          <div><Label className="mb-1 block text-xs">Fecha recepción</Label><Input data-testid="redeco-fec-rec" type="date" value={form.fecha_recepcion} onChange={(e) => setF("fecha_recepcion", e.target.value)} /></div>
          <div><Label className="mb-1 block text-xs">Medio</Label>
            <select data-testid="redeco-medio" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.medio_recepcion_clave} onChange={(e) => setF("medio_recepcion_clave", Number(e.target.value) || "")}>
              <option value="">—</option>
              {medios.map((m) => <option key={m.clave} value={m.clave}>{m.clave} · {m.descripcion}</option>)}
            </select></div>
          <div><Label className="mb-1 block text-xs">Nivel de atención</Label>
            <select data-testid="redeco-nivel" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.nivel_atencion_clave} onChange={(e) => setF("nivel_atencion_clave", Number(e.target.value) || "")}>
              <option value="">—</option>
              {niveles.map((n) => <option key={n.clave} value={n.clave}>{n.clave} · {n.descripcion}</option>)}
            </select></div>
          <div>
            <Label className="mb-1 block text-xs" title="pendiente de sincronizar catálogo">Causa (pendiente de sincronizar catálogo)</Label>
            <Input data-testid="redeco-causa" value={form.causa} onChange={(e) => setF("causa", e.target.value)} placeholder="clave 4 chars" maxLength={4} />
          </div>
          <div><Label className="mb-1 block text-xs">PORI</Label>
            <select data-testid="redeco-pori" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.pori} onChange={(e) => setF("pori", e.target.value)}><option>NO</option><option>SI</option></select></div>
          <div><Label className="mb-1 block text-xs">Estatus</Label>
            <select data-testid="redeco-estatus" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.estatus_numerico} onChange={(e) => setF("estatus_numerico", Number(e.target.value))}>
              <option value={1}>1 · Pendiente</option><option value={2}>2 · Concluido</option>
            </select></div>
          <div><Label className="mb-1 block text-xs">Estado</Label>
            <select data-testid="redeco-estado" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.estado_clave} onChange={(e) => { setF("estado_clave", Number(e.target.value) || ""); setF("codigo_postal",""); setF("municipio_clave",""); setF("colonia_clave",""); }}>
              <option value="">—</option>
              {estados.map((s) => <option key={s.clave} value={s.clave}>{s.clave} · {s.descripcion}</option>)}
            </select></div>
          <div><Label className="mb-1 block text-xs">CP</Label>
            <select data-testid="redeco-cp" disabled={!form.estado_clave} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.codigo_postal} onChange={(e) => { setF("codigo_postal", Number(e.target.value) || ""); setF("municipio_clave",""); setF("colonia_clave",""); }}>
              <option value="">— (elige estado)</option>
              {cps.slice(0, 3000).map((c) => <option key={`${c.clave}-${c.padre}`} value={c.clave}>{String(c.clave).padStart(5, "0")}</option>)}
            </select></div>
          <div><Label className="mb-1 block text-xs">Municipio</Label>
            <select data-testid="redeco-mun" disabled={!form.codigo_postal} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.municipio_clave} onChange={(e) => setF("municipio_clave", Number(e.target.value) || "")}>
              <option value="">—</option>
              {munis.map((m) => <option key={m.clave} value={m.clave}>{m.clave} · {m.descripcion}</option>)}
            </select></div>
          <div><Label className="mb-1 block text-xs">Colonia</Label>
            <select data-testid="redeco-col" disabled={!form.codigo_postal} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.colonia_clave} onChange={(e) => setF("colonia_clave", Number(e.target.value) || "")}>
              <option value="">—</option>
              {cols.map((c) => <option key={`${c.clave}-${c.padre}`} value={c.clave}>{c.clave} · {c.descripcion}</option>)}
            </select></div>
          <div><Label className="mb-1 block text-xs">Tipo persona</Label>
            <select data-testid="redeco-tp" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.tipo_persona} onChange={(e) => setF("tipo_persona", Number(e.target.value))}>
              <option value={1}>1 · Física</option><option value={2}>2 · Moral</option>
            </select></div>
          {Number(form.tipo_persona) === 1 && (<>
            <div><Label className="mb-1 block text-xs">Sexo (H/M)</Label>
              <select data-testid="redeco-sexo" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={form.sexo} onChange={(e) => setF("sexo", e.target.value)}><option value="">—</option><option>H</option><option>M</option></select></div>
            <div><Label className="mb-1 block text-xs">Edad</Label><Input data-testid="redeco-edad" type="number" min="0" max="120" value={form.edad} onChange={(e) => setF("edad", e.target.value)} /></div>
          </>)}
          <div><Label className="mb-1 block text-xs">Fecha resolución</Label><Input data-testid="redeco-fec-res" type="date" value={form.fecha_resolucion} onChange={(e) => setF("fecha_resolucion", e.target.value)} /></div>
          <div><Label className="mb-1 block text-xs">Fecha notificación</Label><Input data-testid="redeco-fec-not" type="date" value={form.fecha_notificacion} onChange={(e) => setF("fecha_notificacion", e.target.value)} /></div>
          {Number(form.estatus_numerico) === 2 && (
            <div><Label className="mb-1 block text-xs">Sentido de resolución (obligatorio si Concluido)</Label>
              <select data-testid="redeco-respuesta" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
                value={form.sentido_resolucion} onChange={(e) => setF("sentido_resolucion", Number(e.target.value) || "")}>
                <option value="">—</option>
                {sentidos.map((s) => <option key={s.clave} value={s.clave}>{s.clave} · {s.descripcion}</option>)}
              </select></div>
          )}
          <div><Label className="mb-1 block text-xs">Num. penalizaciones</Label><Input data-testid="redeco-numpenal" type="number" value={form.num_penalizaciones} onChange={(e) => setF("num_penalizaciones", e.target.value)} /></div>
          <div><Label className="mb-1 block text-xs">Penalización (1/2/3)</Label>
            <select data-testid="redeco-penal" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={form.penalizacion} onChange={(e) => setF("penalizacion", Number(e.target.value) || "")}>
              <option value="">—</option><option value={1}>1 · Cancelación</option><option value={2}>2 · Reasignación</option><option value={3}>3 · Multa</option>
            </select></div>
          <div><Label className="mb-1 block text-xs">Cliente (id expediente)</Label><Input data-testid="redeco-cliente-id" value={form.cliente_id} onChange={(e) => setF("cliente_id", e.target.value)} placeholder="autocompleta tipo/sexo/edad/dirección" /></div>
          <div><Label className="mb-1 block text-xs">Producto (id · trae RECA)</Label><Input data-testid="redeco-prod-id" value={form.producto_id} onChange={(e) => setF("producto_id", e.target.value)} /></div>
          <div><Label className="mb-1 block text-xs">Despacho</Label><Input data-testid="redeco-despacho" value={form.despacho} onChange={(e) => setF("despacho", e.target.value)} /></div>
          <div><Label className="mb-1 block text-xs">Motivo (interno)</Label><Input data-testid="redeco-motivo" value={form.motivo} onChange={(e) => setF("motivo", e.target.value)} /></div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="redeco-preview-btn" onClick={armarPreview} disabled={busy}>
            <Info className="mr-1 h-4 w-4" /> Ver datos para captura manual (25 campos)
          </Button>
          {conexionReal && (
            <Button data-testid="redeco-enviar-btn" className="bg-forest-600 hover:bg-forest-700" onClick={() => setConfirmEnvio(true)} disabled={busy}>
              <ArrowRight className="mr-1 h-4 w-4" /> Enviar a CONDUSEF ({modo})
            </Button>
          )}
          <a href="https://pur.condusef.gob.mx" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-forest-700 underline">
            <ExternalLink className="h-3 w-3" /> Portal CONDUSEF
          </a>
        </div>
      </div>

      {showPreview && preview && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="redeco-preview-panel">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-heading text-sm font-semibold text-slate-900">Captura manual — 25 campos con clave oficial</h4>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid="redeco-copiar-todo" onClick={copiarTodo}>Copiar todo (TSV)</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowPreview(false)}>Cerrar</Button>
            </div>
          </div>
          {preview.producto_reca_pendiente && (
            <Alert className="mb-2 border-amber-300 bg-amber-50"><AlertTriangle className="h-4 w-4 text-amber-700" /><AlertDescription className="text-xs">El producto seleccionado tiene RECA pendiente de capturar.</AlertDescription></Alert>
          )}
          {errsBackend.length > 0 && (
            <Alert className="mb-2 border-red-300 bg-red-50"><AlertTriangle className="h-4 w-4 text-red-700" /><AlertDescription className="text-xs">Validaciones: {errsBackend.join(" · ")}</AlertDescription></Alert>
          )}
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <th className="py-1 pl-1">Campo</th><th className="py-1">Tipo</th><th className="py-1">Valor</th><th className="py-1">Copiar</th></tr></thead>
            <tbody>
              {LAYOUT_HDRS.map(([k, tipo, oblig, hint]) => {
                const v = preview.fila[k];
                const vTxt = v === null || v === undefined ? "" : String(v);
                return (
                  <tr key={k} data-testid={`redeco-preview-row-${k}`} className="border-b border-slate-100">
                    <td className="py-1 pl-1 font-mono text-xs">
                      {k}{oblig && <span className="ml-1 text-red-500">*</span>}
                      <div className="text-[10px] text-slate-400">{hint}</div>
                    </td>
                    <td className="py-1 text-xs text-slate-500">{tipo}</td>
                    <td className="py-1 font-mono text-xs">{vTxt === "" ? <span className="text-slate-400">null</span> : vTxt}</td>
                    <td className="py-1">
                      <button className="text-xs text-forest-700 underline" data-testid={`redeco-copiar-${k}`} onClick={() => copiarCampo(k, vTxt)}>copiar</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirmEnvio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="redeco-confirm-modal">
          <div className="w-[420px] rounded-lg bg-white p-5 shadow-xl">
            <h4 className="font-heading text-base font-semibold text-slate-900">Confirmar envío a CONDUSEF</h4>
            <p className="mt-2 text-sm text-slate-600">
              Estás por enviar esta queja REDECO a CONDUSEF en <strong>{modo}</strong>. Es un envío real a la autoridad y quedará registrado en bitácora.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmEnvio(false)}>Cancelar</Button>
              <Button className="bg-forest-600 hover:bg-forest-700" data-testid="redeco-confirm-ok" onClick={enviarConfirmado}>Confirmar y enviar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Los 25 campos con etiquetas (misma fuente que backend `LAYOUT_REDECO_V4`).
const LAYOUT_HDRS = [
  ["QuejasDenominacion", "alfanumérico 400", true,  "Razón social SIPRES/REUNE"],
  ["QuejasSector",       "alfanumérico 200", true,  "SOFOM E.N.R."],
  ["QuejasNoMes",        "numérico 2",       true,  "1=Enero ... 12=Diciembre"],
  ["QuejasNum",          "numérico 1",       true,  "Siempre 1"],
  ["QuejasFolio",        "alfanumérico 50",  true,  "Único por entidad"],
  ["QuejasFecRecepcion", "fecha dd/mm/aaaa", true,  "Dentro del mes reportado"],
  ["QuejasMedio",        "numérico 2",       true,  "MEDIO_RECEPCION"],
  ["QuejasNivelAT",      "numérico 2",       true,  "NIVEL_ATENCION"],
  ["QuejasProducto",     "alfanumérico 12",  true,  "Del RECA del producto"],
  ["QuejasCausa",        "alfanumérico 4",   true,  "Catálogo CAUSA (pendiente)"],
  ["QuejasPORI",         "alfanumérico 2",   true,  "SI/NO en mayúsculas"],
  ["QuejasEstatus",      "numérico 1",       true,  "1=Pendiente, 2=Concluido"],
  ["QuejasEstados",      "numérico 2",       true,  "ENTIDAD_FEDERATIVA"],
  ["QuejasMunId",        "numérico 8",       true,  "MUNICIPIO"],
  ["QuejasLocId",        "numérico 8",       false, "Opcional"],
  ["QuejasColId",        "numérico 8",       true,  "COLONIA"],
  ["QuejasCP",           "numérico 10",      true,  "Sin ceros a la izq."],
  ["QuejasTipoPersona",  "numérico 1",       true,  "1=Física, 2=Moral"],
  ["QuejasSexo",         "alfanumérico 1",   false, "H/M, sólo PF"],
  ["QuejasEdad",         "numérico 3",       false, "Sólo PF"],
  ["QuejasFecResolucion","fecha dd/mm/aaaa", false, "Opcional"],
  ["QuejasFecNotificacion","fecha dd/mm/aaaa",false,"Opcional"],
  ["QuejasRespuesta",    "numérico 1",       false, "Obligatorio si Estatus=2"],
  ["QuejasNumPenal",     "numérico 4",       false, "Penalizaciones despacho"],
  ["QuejasPenalizacion", "numérico 1",       false, "1/2/3"],
];


// ═════════════════════════════════════════════════════════════════════════════
// REUNE — Consultas / Reclamaciones / Aclaraciones GENERAL (trimestral SOFOM ENR).
// Sigue el mismo patrón de RedecoBloque: captura + preview con claves oficiales,
// funciona con o sin API. El botón "Enviar" sólo aparece si hay conexión real.
// El cierre trimestral NO se hace por API: aviso al portal + upload de acuse.
// ═════════════════════════════════════════════════════════════════════════════
function ReuneBloque({ cond }) {
  const modo = cond?.modo || "mock";
  const conexionReal = modo === "sandbox" || modo === "produccion";

  const [tipo, setTipo] = useState("reclamaciones");
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [trimestre, setTrimestre] = useState(Math.ceil((new Date().getMonth() + 1) / 3));

  // Catálogos
  const [estados, setEstados] = useState([]);
  const [medios, setMedios] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [sentidos, setSentidos] = useState([]);
  const [cps, setCps] = useState([]);
  const [munis, setMunis] = useState([]);
  const [cols, setCols] = useState([]);

  // Form (superset — se filtran por tipo al enviar)
  const [form, setForm] = useState({
    folio: "",
    fecha_recepcion: "",
    fecha_reclamacion: "",
    fecha_aclaracion: "",
    fecha_atencion: "",
    fecha_resolucion: "",
    fecha_notificacion: "",
    fecha_abono: "",
    medio_recepcion_clave: "",
    nivel_atencion_clave: "",
    causa: "",
    pori: "NO",
    estatus_numerico: 1,
    estado_clave: "",
    codigo_postal: "",
    municipio_clave: "",
    colonia_clave: "",
    localidad_clave: "",
    tipo_persona: 1,
    sexo: "",
    edad: "",
    sentido_resolucion: "",
    monetario: "NO",
    monto_reclamado: "",
    importe_abonado: "",
    folio_condusef: "",
    reversa_sige: "",
    operacion_extranjero: "NO",
    cliente_id: "",
    producto_id: "",
  });

  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errsBackend, setErrsBackend] = useState([]);
  const [confirmEnvio, setConfirmEnvio] = useState(false);
  const [contadores, setContadores] = useState({ por_tipo: { consultas: 0, reclamaciones: 0, aclaraciones: 0 }, total: 0, aviso_cero: true });
  const [acuse, setAcuse] = useState(null);
  const [acuseFile, setAcuseFile] = useState(null);
  const [acuseSubiendo, setAcuseSubiendo] = useState(false);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Cargar catálogos base
  useEffect(() => {
    api.get("/catalogos-condusef/ENTIDAD_FEDERATIVA").then((r) => setEstados(r.data.registros || [])).catch(() => {});
    api.get("/catalogos-condusef/MEDIO_RECEPCION").then((r) => setMedios(r.data.registros || [])).catch(() => {});
    api.get("/catalogos-condusef/NIVEL_ATENCION").then((r) => setNiveles(r.data.registros || [])).catch(() => {});
    api.get("/catalogos-condusef/SENTIDO_RESOLUCION").then((r) => setSentidos(r.data.registros || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.estado_clave) { setCps([]); setMunis([]); setCols([]); return; }
    api.get(`/catalogos-condusef/CODIGO_POSTAL?padre=${form.estado_clave}&limit=8000`)
      .then((r) => setCps(r.data.registros || [])).catch(() => {});
    api.get(`/catalogos-condusef/MUNICIPIO?padre=${form.estado_clave}&limit=8000`)
      .then((r) => setMunis(r.data.registros || [])).catch(() => {});
  }, [form.estado_clave]);

  useEffect(() => {
    if (!form.codigo_postal) { setCols([]); return; }
    api.get(`/catalogos-condusef/COLONIA?padre=${form.codigo_postal}&limit=2000`)
      .then((r) => setCols(r.data.registros || [])).catch(() => {});
  }, [form.codigo_postal]);

  // Contadores y acuse por trimestre
  useEffect(() => {
    api.get(`/reune/contadores?anio=${anio}&trimestre=${trimestre}`)
      .then((r) => setContadores(r.data)).catch(() => {});
    api.get(`/reune/cierre/${anio}/${trimestre}`)
      .then((r) => setAcuse(r.data)).catch(() => {});
  }, [anio, trimestre]);

  const armarPreview = async () => {
    setBusy(true);
    try {
      // Nombre del campo de fecha principal por tipo
      const fechaPrincipalKey = tipo === "consultas" ? "fecha_recepcion"
                              : tipo === "reclamaciones" ? "fecha_reclamacion"
                              : "fecha_aclaracion";
      const payload = { tipo, trimestre, anio,
        queja: { ...form, trimestre, anio,
                 fecha_recepcion: form[fechaPrincipalKey] || form.fecha_recepcion } };
      const { data } = await api.post("/reune/preview-layout", payload);
      setPreview(data);
      setErrsBackend(data.errores_validacion || []);
      setShowPreview(true);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const copiarCampo = (nombre, valor) => {
    const t = valor === null || valor === undefined ? "" : String(valor);
    navigator.clipboard.writeText(t).then(() => toast.success(`${nombre} copiado.`));
  };
  const copiarTodo = () => {
    if (!preview) return;
    const filas = preview.layout.map(([k]) => `${k}\t${preview.fila[k] ?? ""}`).join("\n");
    navigator.clipboard.writeText(filas).then(() => toast.success(`${preview.layout.length} campos copiados (TSV).`));
  };

  const enviarConfirmado = async () => {
    setConfirmEnvio(false); setBusy(true);
    try {
      const { data } = await api.post("/reune/enviar", { tipo, quejas: [{ ...form, trimestre, anio }] });
      toast.success(`Envío REUNE ${tipo} — ${data.ok ? "OK" : "con observaciones"} · acuse: ${data.acuse || "(sin acuse remoto)"}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const subirAcuse = async () => {
    if (!acuseFile) { toast.error("Selecciona el archivo del acuse descargado del portal."); return; }
    setAcuseSubiendo(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const rd = new FileReader();
        rd.onload = () => resolve(rd.result);
        rd.onerror = reject;
        rd.readAsDataURL(acuseFile);
      });
      const { data } = await api.post("/reune/cierre/acuse", { anio, trimestre, nombre: acuseFile.name, archivo_base64: b64 });
      toast.success(`Acuse ${data.nombre} subido para ${anio}-T${trimestre}.`);
      setAcuse({ ...data, acuse_presente: true });
      setAcuseFile(null);
    } catch (e) { toast.error(apiError(e)); } finally { setAcuseSubiendo(false); }
  };

  // Etiquetas por tipo para el formulario
  const labels = {
    consultas: { fechaPrincipal: "Fecha recepción", folio: "Folio consulta" },
    reclamaciones: { fechaPrincipal: "Fecha reclamación", folio: "Folio atención" },
    aclaraciones: { fechaPrincipal: "Fecha aclaración", folio: "Folio atención" },
  }[tipo];
  const fechaPrincipalKey = tipo === "consultas" ? "fecha_recepcion"
                          : tipo === "reclamaciones" ? "fecha_reclamacion"
                          : "fecha_aclaracion";

  return (
    <div className="space-y-4">
      {/* Contadores + Cierre trimestral */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="reune-contadores-card">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold text-slate-900">Contador trimestral · {anio}-T{trimestre}</h3>
            <div className="flex gap-2">
              <select data-testid="reune-anio" className="rounded-md border border-slate-200 px-2 py-1 text-sm"
                value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
                {[anio - 1, anio, anio + 1].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <select data-testid="reune-trimestre" className="rounded-md border border-slate-200 px-2 py-1 text-sm"
                value={trimestre} onChange={(e) => setTrimestre(Number(e.target.value))}>
                <option value={1}>T1</option><option value={2}>T2</option><option value={3}>T3</option><option value={4}>T4</option>
              </select>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
            <div className="rounded border border-slate-200 p-2 text-center" data-testid="reune-cnt-consultas">
              <div className="text-xs text-slate-500">Consultas</div>
              <div className="text-lg font-mono">{contadores.por_tipo?.consultas ?? 0}</div>
            </div>
            <div className="rounded border border-slate-200 p-2 text-center" data-testid="reune-cnt-reclamaciones">
              <div className="text-xs text-slate-500">Reclamaciones</div>
              <div className="text-lg font-mono">{contadores.por_tipo?.reclamaciones ?? 0}</div>
            </div>
            <div className="rounded border border-slate-200 p-2 text-center" data-testid="reune-cnt-aclaraciones">
              <div className="text-xs text-slate-500">Aclaraciones</div>
              <div className="text-lg font-mono">{contadores.por_tipo?.aclaraciones ?? 0}</div>
            </div>
          </div>
          {contadores.aviso_cero && (
            <Alert className="mt-3 border-amber-300 bg-amber-50" data-testid="reune-aviso-cero">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-xs">
                Sin movimientos en el trimestre. Recuerda: aunque no haya operaciones, CONDUSEF exige reportar en ceros.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="reune-cierre-card">
          <h3 className="font-heading text-base font-semibold text-slate-900">Cierre trimestral · acuse manual</h3>
          <p className="mt-1 text-xs text-slate-500">
            El cierre trimestral se realiza en el portal CONDUSEF; ahí se emite el acuse. Aquí lo subes para dejar rastro.
          </p>
          <a href="https://pur.condusef.gob.mx" target="_blank" rel="noreferrer"
             className="mt-2 inline-flex items-center gap-1 text-xs text-forest-700 underline" data-testid="reune-portal-link">
            <ExternalLink className="h-3 w-3" /> Ir al portal pur.condusef.gob.mx
          </a>
          <div className="mt-3 flex items-center gap-2">
            <input type="file" accept="application/pdf,image/*" data-testid="reune-acuse-file"
              onChange={(e) => setAcuseFile(e.target.files?.[0] || null)}
              className="text-xs" />
            <Button size="sm" data-testid="reune-acuse-subir" onClick={subirAcuse} disabled={acuseSubiendo || !acuseFile}>
              <Upload className="mr-1 h-4 w-4" /> Subir acuse
            </Button>
          </div>
          {acuse?.acuse_presente && (
            <div className="mt-2 rounded border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-800" data-testid="reune-acuse-actual">
              Acuse cargado: <strong>{acuse.nombre}</strong> · subido por {acuse.subido_por || "—"}.
            </div>
          )}
        </div>
      </div>

      {/* Formulario de alta */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="reune-form-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-heading text-base font-semibold text-slate-900">Alta REUNE — layout GENERAL v4.0</h3>
          <div className="flex gap-2">
            <select data-testid="reune-tipo" className="rounded-md border border-slate-200 px-2 py-1 text-sm"
              value={tipo} onChange={(e) => { setTipo(e.target.value); setShowPreview(false); }}>
              <option value="consultas">Consulta</option>
              <option value="reclamaciones">Reclamación</option>
              <option value="aclaraciones">Aclaración</option>
            </select>
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-500">Selects encadenados desde <code>catalogos_condusef</code>. La vista de captura manual funciona con o sin API.</p>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div><Label className="mb-1 block text-xs">{labels.folio}</Label>
            <Input data-testid="reune-folio" value={form.folio} onChange={(e) => setF("folio", e.target.value)} /></div>
          <div><Label className="mb-1 block text-xs">{labels.fechaPrincipal}</Label>
            <Input data-testid="reune-fec-principal" type="date" value={form[fechaPrincipalKey]}
              onChange={(e) => setF(fechaPrincipalKey, e.target.value)} /></div>
          <div><Label className="mb-1 block text-xs">Medio</Label>
            <select data-testid="reune-medio" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.medio_recepcion_clave} onChange={(e) => setF("medio_recepcion_clave", Number(e.target.value) || "")}>
              <option value="">—</option>
              {medios.map((m) => <option key={m.clave} value={m.clave}>{m.clave} · {m.descripcion}</option>)}
            </select></div>
          <div><Label className="mb-1 block text-xs">Estatus</Label>
            <select data-testid="reune-estatus" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.estatus_numerico} onChange={(e) => setF("estatus_numerico", Number(e.target.value))}>
              <option value={1}>1 · Pendiente</option><option value={2}>2 · Concluido</option>
            </select></div>
          <div><Label className="mb-1 block text-xs" title="pendiente de sincronizar catálogo">Causa (pendiente de sincronizar catálogo)</Label>
            <Input data-testid="reune-causa" maxLength={4} value={form.causa} onChange={(e) => setF("causa", e.target.value)} placeholder="clave 4 chars" /></div>
          <div><Label className="mb-1 block text-xs">PORI</Label>
            <select data-testid="reune-pori" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.pori} onChange={(e) => setF("pori", e.target.value)}><option>NO</option><option>SI</option></select></div>
          <div><Label className="mb-1 block text-xs">Estado</Label>
            <select data-testid="reune-estado" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.estado_clave} onChange={(e) => { setF("estado_clave", Number(e.target.value) || ""); setF("codigo_postal",""); setF("municipio_clave",""); setF("colonia_clave",""); }}>
              <option value="">—</option>
              {estados.map((s) => <option key={s.clave} value={s.clave}>{s.clave} · {s.descripcion}</option>)}
            </select></div>
          <div><Label className="mb-1 block text-xs">CP</Label>
            <select data-testid="reune-cp" disabled={!form.estado_clave} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.codigo_postal} onChange={(e) => { setF("codigo_postal", Number(e.target.value) || ""); setF("colonia_clave",""); }}>
              <option value="">— (elige estado)</option>
              {cps.slice(0, 3000).map((c) => <option key={`${c.clave}-${c.padre}`} value={c.clave}>{String(c.clave).padStart(5, "0")}</option>)}
            </select></div>
          <div><Label className="mb-1 block text-xs">Municipio</Label>
            <select data-testid="reune-mun" disabled={!form.estado_clave} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.municipio_clave} onChange={(e) => setF("municipio_clave", Number(e.target.value) || "")}>
              <option value="">—</option>
              {munis.map((m) => <option key={m.clave} value={m.clave}>{m.clave} · {m.descripcion}</option>)}
            </select></div>
          <div><Label className="mb-1 block text-xs">Colonia</Label>
            <select data-testid="reune-col" disabled={!form.codigo_postal} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.colonia_clave} onChange={(e) => setF("colonia_clave", Number(e.target.value) || "")}>
              <option value="">—</option>
              {cols.map((c) => <option key={`${c.clave}-${c.padre}`} value={c.clave}>{c.clave} · {c.descripcion}</option>)}
            </select></div>
          <div><Label className="mb-1 block text-xs">Tipo persona</Label>
            <select data-testid="reune-tp" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={form.tipo_persona} onChange={(e) => setF("tipo_persona", Number(e.target.value))}>
              <option value={1}>1 · Física</option><option value={2}>2 · Moral</option>
            </select></div>
          {Number(form.tipo_persona) === 1 && (<>
            <div><Label className="mb-1 block text-xs">Sexo (H/M)</Label>
              <select data-testid="reune-sexo" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
                value={form.sexo} onChange={(e) => setF("sexo", e.target.value)}><option value="">—</option><option>H</option><option>M</option></select></div>
            <div><Label className="mb-1 block text-xs">Edad (≥18)</Label>
              <Input data-testid="reune-edad" type="number" min="18" max="120" value={form.edad} onChange={(e) => setF("edad", e.target.value)} /></div>
          </>)}
          {Number(form.estatus_numerico) === 2 && (<>
            <div><Label className="mb-1 block text-xs">Fecha atención</Label>
              <Input data-testid="reune-fec-aten" type="date" value={form.fecha_atencion} onChange={(e) => setF("fecha_atencion", e.target.value)} /></div>
            <div><Label className="mb-1 block text-xs">Nivel atención</Label>
              <select data-testid="reune-nivel" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
                value={form.nivel_atencion_clave} onChange={(e) => setF("nivel_atencion_clave", Number(e.target.value) || "")}>
                <option value="">—</option>
                {niveles.map((n) => <option key={n.clave} value={n.clave}>{n.clave} · {n.descripcion}</option>)}
              </select></div>
            {tipo !== "consultas" && (<>
              <div><Label className="mb-1 block text-xs">Fecha resolución</Label>
                <Input data-testid="reune-fec-res" type="date" value={form.fecha_resolucion} onChange={(e) => setF("fecha_resolucion", e.target.value)} /></div>
              <div><Label className="mb-1 block text-xs">Fecha notificación</Label>
                <Input data-testid="reune-fec-not" type="date" value={form.fecha_notificacion} onChange={(e) => setF("fecha_notificacion", e.target.value)} /></div>
              <div><Label className="mb-1 block text-xs">Sentido resolución</Label>
                <select data-testid="reune-sentido" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
                  value={form.sentido_resolucion} onChange={(e) => setF("sentido_resolucion", Number(e.target.value) || "")}>
                  <option value="">—</option>
                  {sentidos.map((s) => <option key={s.clave} value={s.clave}>{s.clave} · {s.descripcion}</option>)}
                </select></div>
            </>)}
          </>)}
          {tipo !== "consultas" && (<>
            <div><Label className="mb-1 block text-xs">Monetario</Label>
              <select data-testid="reune-monetario" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
                value={form.monetario} onChange={(e) => setF("monetario", e.target.value)}><option>NO</option><option>SI</option></select></div>
            {form.monetario === "SI" && (
              <div><Label className="mb-1 block text-xs">Monto reclamado</Label>
                <Input data-testid="reune-monto" type="number" min="0" step="0.01"
                  value={form.monto_reclamado} onChange={(e) => setF("monto_reclamado", e.target.value)} /></div>
            )}
            {tipo === "reclamaciones" && form.monetario === "SI" && Number(form.estatus_numerico) === 2 && (<>
              <div><Label className="mb-1 block text-xs">Importe abonado</Label>
                <Input data-testid="reune-abono" type="number" min="0" step="0.01"
                  value={form.importe_abonado} onChange={(e) => setF("importe_abonado", e.target.value)} /></div>
              <div><Label className="mb-1 block text-xs">Fecha abono (si &gt; 0)</Label>
                <Input data-testid="reune-fec-abono" type="date" value={form.fecha_abono} onChange={(e) => setF("fecha_abono", e.target.value)} /></div>
            </>)}
          </>)}
          {[6, 7].includes(Number(form.medio_recepcion_clave)) && (
            <div><Label className="mb-1 block text-xs">Folio CONDUSEF (medio 6/7)</Label>
              <Input data-testid="reune-folio-condusef" value={form.folio_condusef} onChange={(e) => setF("folio_condusef", e.target.value)} /></div>
          )}
          {Number(form.medio_recepcion_clave) === 6 && (
            <div><Label className="mb-1 block text-xs">Reversa SIGE</Label>
              <select data-testid="reune-reversa" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
                value={form.reversa_sige} onChange={(e) => setF("reversa_sige", Number(e.target.value))}>
                <option value="">—</option><option value={0}>0 · No SIGE</option><option value={1}>1 · Sí SIGE</option>
              </select></div>
          )}
          {tipo === "aclaraciones" && (
            <div><Label className="mb-1 block text-xs">Operación en el extranjero</Label>
              <select data-testid="reune-op-extranjero" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
                value={form.operacion_extranjero} onChange={(e) => setF("operacion_extranjero", e.target.value)}><option>NO</option><option>SI</option></select></div>
          )}
          <div><Label className="mb-1 block text-xs">Cliente (id expediente)</Label>
            <Input data-testid="reune-cliente-id" value={form.cliente_id} onChange={(e) => setF("cliente_id", e.target.value)}
              placeholder="autocompleta tipo/sexo/edad" /></div>
          <div><Label className="mb-1 block text-xs">Producto (id · trae RECA)</Label>
            <Input data-testid="reune-prod-id" value={form.producto_id} onChange={(e) => setF("producto_id", e.target.value)} /></div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="reune-preview-btn" onClick={armarPreview} disabled={busy}>
            <Info className="mr-1 h-4 w-4" /> Ver datos para captura manual
          </Button>
          {conexionReal && (
            <Button data-testid="reune-enviar-btn" className="bg-forest-600 hover:bg-forest-700"
              onClick={() => setConfirmEnvio(true)} disabled={busy}>
              <ArrowRight className="mr-1 h-4 w-4" /> Enviar a CONDUSEF ({modo})
            </Button>
          )}
          <a href="https://pur.condusef.gob.mx" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 text-xs text-forest-700 underline">
            <ExternalLink className="h-3 w-3" /> Portal CONDUSEF
          </a>
        </div>
      </div>

      {/* Preview: 18 / 28 / 26 / 30 campos oficiales */}
      {showPreview && preview && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="reune-preview-panel">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-heading text-sm font-semibold text-slate-900">
              Captura manual — {preview.layout.length} campos con clave oficial ({preview.tipo})
            </h4>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid="reune-copiar-todo" onClick={copiarTodo}>Copiar todo (TSV)</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowPreview(false)}>Cerrar</Button>
            </div>
          </div>
          {preview.producto_reca_pendiente && (
            <Alert className="mb-2 border-amber-300 bg-amber-50"><AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-xs">El producto seleccionado tiene RECA pendiente de capturar.</AlertDescription></Alert>
          )}
          {errsBackend.length > 0 && (
            <Alert className="mb-2 border-red-300 bg-red-50"><AlertTriangle className="h-4 w-4 text-red-700" />
              <AlertDescription className="text-xs">Validaciones: {errsBackend.join(" · ")}</AlertDescription></Alert>
          )}
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <th className="py-1 pl-1">Campo</th><th className="py-1">Tipo</th><th className="py-1">Valor</th><th className="py-1">Copiar</th></tr></thead>
            <tbody>
              {preview.layout.map(([k, tipoDato, oblig, hint]) => {
                const v = preview.fila[k];
                const vTxt = v === null || v === undefined ? "" : String(v);
                return (
                  <tr key={k} data-testid={`reune-preview-row-${k}`} className="border-b border-slate-100">
                    <td className="py-1 pl-1 font-mono text-xs">
                      {k}{oblig && <span className="ml-1 text-red-500">*</span>}
                      <div className="text-[10px] text-slate-400">{hint}</div>
                    </td>
                    <td className="py-1 text-xs text-slate-500">{tipoDato}</td>
                    <td className="py-1 font-mono text-xs">{vTxt === "" ? <span className="text-slate-400">null</span> : vTxt}</td>
                    <td className="py-1">
                      <button className="text-xs text-forest-700 underline" data-testid={`reune-copiar-${k}`}
                        onClick={() => copiarCampo(k, vTxt)}>copiar</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirmEnvio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="reune-confirm-modal">
          <div className="w-[440px] rounded-lg bg-white p-5 shadow-xl">
            <h4 className="font-heading text-base font-semibold text-slate-900">Confirmar envío REUNE</h4>
            <p className="mt-2 text-sm text-slate-600">
              Estás por enviar una {tipo === "consultas" ? "consulta" : tipo === "reclamaciones" ? "reclamación" : "aclaración"} REUNE a CONDUSEF en <strong>{modo}</strong>. El cuerpo va como arreglo pelón y si un folio falla se rechaza el lote completo.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmEnvio(false)}>Cancelar</Button>
              <Button className="bg-forest-600 hover:bg-forest-700" data-testid="reune-confirm-ok" onClick={enviarConfirmado}>Confirmar y enviar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


